
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Star, Trophy } from 'lucide-react';
import { LevelConfig } from '@/models/LearnTypes';
import { useAuth } from '@/contexts/AuthContext';

interface LessonCompleteProps {
  totalXpGained: number;
  levels: LevelConfig[];
  lessonName: string;
  onBackToLessons: () => void;
  isPerfectLesson?: boolean; // Whether all levels in the lesson were completed perfectly
  lessonBonus?: number; // XP bonus for perfect lesson completion
  levelCompletionCounts?: Record<number, number>; // Number of times each level has been completed
  triggerAchievementCheck?: (trigger: boolean) => void; // Function to trigger achievement check
  isDailyQuest?: boolean; // Whether this is a daily quest lesson
  dailyQuestBonus?: number; // XP bonus for completing a daily quest
}

const LessonComplete = ({ 
  totalXpGained, 
  levels, 
  lessonName, 
  onBackToLessons,
  isPerfectLesson = false,
  lessonBonus = 0,
  levelCompletionCounts = {},
  triggerAchievementCheck,
  isDailyQuest = false,
  dailyQuestBonus = 0
}: LessonCompleteProps) => {
  const { user } = useAuth();
  
  // Debug logging for perfect lesson status
  useEffect(() => {
    console.debug(`[LessonComplete] Component mounted with isPerfectLesson=${isPerfectLesson}, lessonBonus=${lessonBonus}`);
    console.debug(`[LessonComplete] Total XP gained: ${totalXpGained}`);
    
    // For debugging: print each level's perfect status
    levels.forEach((level, index) => {
      console.debug(`[LessonComplete] Level ${index} (${level.title}) - perfect: ${level.isPerfect}, state: ${level.state}`);
    });
    
    return () => {
      console.debug(`[LessonComplete] Component unmounting with isPerfectLesson=${isPerfectLesson}, lessonBonus=${lessonBonus}`);
    };
  }, []);
  
  // For debugging purposes, check the window-stored perfect level statuses
  useEffect(() => {
    for (let i = 0; i < levels.length; i++) {
      const windowPerfectStatus = (window as any)[`level_${i}_perfect_status`];
      const storagePerfectStatus = localStorage.getItem(`level_${i}_perfect_status`);
      console.debug(`[LessonComplete] Level ${i} status from window: ${windowPerfectStatus}, from storage: ${storagePerfectStatus}`);
    }
    
    const globalPerfectLesson = (window as any).__PERFECT_LESSON__;
    console.debug(`[LessonComplete] Global perfect lesson status: ${globalPerfectLesson}`);
  }, [levels]);
  
  // Find the level 3 (index 2) and check if it had a perfect spelling bonus
  const level3 = levels[2];
  const hasPerfectSpellingBonus = level3?.type === 'spelling' && 
                                  level3?.state === 'completed' && 
                                  level3?.isPerfect === true;
                                  
  // Function to calculate adjusted XP for a level based on completion count
  const getAdjustedXpForLevel = (levelIndex: number) => {
    const level = levels[levelIndex];
    if (!level) return 0;
    
    const originalXP = level.xpReward || 0;
    const completionCount = levelCompletionCounts[levelIndex] || 0;
    
    // Calculate the XP with decay applied
    const adjustedXP = Math.max(1, Math.ceil(originalXP / (completionCount + 1)));
    
    // Perfect level bonus (5 XP) is not affected by decay, so we don't include it here
    // It will be shown separately with the perfect level badge
    
    console.debug(`[LessonComplete] Level ${levelIndex} XP calculation: original=${originalXP}, completions=${completionCount}, adjusted=${adjustedXP}`);
    return adjustedXP;
  };

  // CRITICAL FIX: Check every possible indicator of perfect lesson status
  
  // Note: We're not using PerfectLevelContext directly as it might not be available
  // Instead, rely on passed props and global flags
  
  // Check UI-level prop passed from parent
  const propPerfectStatus = isPerfectLesson === true;
  
  // Check global flag
  const globalPerfectStatus = (window as any).__PERFECT_LESSON__ === true;
  
  // Check localStorage for this specific lesson
  let localStoragePerfectStatus = true; // Default to true
  try {
    // Check generic perfect lesson flags
    const perfectLessonKey = 'perfect_lesson_status';
    const perfectLessonStatus = localStorage.getItem(perfectLessonKey) === 'true';
    console.log(`[LessonComplete] Global perfect lesson status from storage: ${perfectLessonStatus}`);
    
    // Look for level-specific flags
    const levelCount = levels.length;
    let allLevelsPerfectInStorage = true;
    
    for (let i = 0; i < levelCount; i++) {
      // Generic level status (not tied to lesson ID)
      const levelStatus = localStorage.getItem(`level_${i}_perfect`) === 'true';
      console.log(`[LessonComplete] Level ${i} storage status: ${levelStatus}`);
      if (levelStatus === false) {
        allLevelsPerfectInStorage = false;
      }
    }
    
    localStoragePerfectStatus = perfectLessonStatus !== false && allLevelsPerfectInStorage;
  } catch (e) {
    console.error('Error checking localStorage for perfect lesson status', e);
  }
  
  // Advanced check: examine levels directly for perfect status
  const levelsWithPerfectData = levels.filter(level => level.isPerfect !== undefined).length;
  const allLevelsMarkedPerfect = levelsWithPerfectData > 0 && 
    levels.every(level => level.state === 'completed' && level.isPerfect !== false);
  
  console.log(`[LessonComplete] Direct level check: ${levelsWithPerfectData} levels with data, allPerfect=${allLevelsMarkedPerfect}`);
  
  // FINAL DETERMINATION: A lesson is perfect if ANY reliable source says it's perfect
  // This is because we should err on the side of rewarding the user
  const finalPerfectLesson = propPerfectStatus || globalPerfectStatus || 
    localStoragePerfectStatus || allLevelsMarkedPerfect;
  
  // Synchronize the perfect status across all storage mechanisms
  // This ensures that activity logging will see the correct status
  (window as any).__PERFECT_LESSON__ = finalPerfectLesson;
  (window as any).__PERFECT_LESSON_DETERMINED__ = true;
  
  try {
    // Store in generic keys that don't require lesson ID
    localStorage.setItem('perfect_lesson_status', String(finalPerfectLesson));
    
    // Store perfect status for each level
    for (let i = 0; i < levels.length; i++) {
      localStorage.setItem(`level_${i}_perfect`, String(finalPerfectLesson));
    }
  } catch (e) {
    console.error('Error setting localStorage perfect lesson status', e);
  }
  
  // CRITICAL FIX: Get the actual bonus amount
  // First check window global, then prop, then default to 10 if perfect
  const storedBonus = (window as any).__PERFECT_LESSON_BONUS__;
  const actualBonus = finalPerfectLesson ? 
    (typeof storedBonus === 'number' && storedBonus > 0 ? storedBonus : 
      (lessonBonus > 0 ? lessonBonus : 10)) : 0;
  
  console.log(`[LessonComplete] Perfect status sources: prop=${propPerfectStatus}, ` + 
    `global=${globalPerfectStatus}, localStorage=${localStoragePerfectStatus}, levels=${allLevelsMarkedPerfect}`);
  console.log(`[LessonComplete] FINAL PERFECT LESSON STATUS: ${finalPerfectLesson}, bonus=${actualBonus}`);
  
  // Get base XP (without bonus)
  const baseXP = (window as any).__BASE_XP_GAINED__ || totalXpGained - actualBonus;
  
  // Calculate the true total XP (base + bonus)
  const trueTotalXP = baseXP + actualBonus;
  
  // Update perfect status on unmount to ensure activity logging has the right value
  useEffect(() => {
    return () => {
      console.log(`[LessonComplete] Unmounting, setting final perfect status: ${finalPerfectLesson}`);
      (window as any).__PERFECT_LESSON__ = finalPerfectLesson;
      (window as any).__PERFECT_LESSON_DETERMINED__ = true;
      (window as any).__PERFECT_LESSON_BONUS__ = actualBonus;
    };
  }, [finalPerfectLesson, actualBonus]);
  
  return (
    <div 
      className="animate-fade-in space-y-6"
      data-perfect-lesson={finalPerfectLesson.toString()}
      data-lesson-bonus={actualBonus.toString()}
    >
      <Card className="border-none shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Lesson Complete!</CardTitle>
          <CardDescription className="text-lg">
            {lessonName}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-8 pt-4">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
            {finalPerfectLesson ? (
              <Trophy className="h-10 w-10 text-yellow-500 animate-bounce" />
            ) : (
              <Sparkles className="h-10 w-10 text-yellow-400" />
            )}
          </div>
          
          {/* Daily Quest badge */}
          {isDailyQuest && (
            <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-full flex items-center gap-2 animate-pulse">
              <Sparkles className="h-4 w-4 text-purple-600" />
              <span className="font-semibold">Daily Quest Completed!</span>
              <Sparkles className="h-4 w-4 text-purple-600" />
            </div>
          )}

          {/* Perfect lesson badge */}
          {finalPerfectLesson && (
            <div className="bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full flex items-center gap-2 animate-pulse">
              <Star className="h-4 w-4 fill-yellow-400 stroke-yellow-600" />
              <span className="font-semibold">Perfect Lesson!</span>
              <Star className="h-4 w-4 fill-yellow-400 stroke-yellow-600" />
            </div>
          )}
          
          <div className="flex flex-col items-center gap-2">
            {/* CRITICAL FIX: Show the TOTAL XP (base + bonus) */}
            <span className="text-3xl font-bold">+{trueTotalXP} XP</span>
            
            {/* Daily Quest bonus display */}
            {isDailyQuest && dailyQuestBonus > 0 && (
              <div className="flex items-center text-purple-600 gap-1 mt-1">
                <span className="text-sm font-medium">Includes +{dailyQuestBonus} daily quest bonus</span>
              </div>
            )}
            
            {/* Perfect lesson bonus display */}
            {finalPerfectLesson && actualBonus > 0 && (
              <div className="flex items-center text-yellow-600 gap-1 mt-1">
                <span className="text-sm font-medium">Includes +{actualBonus} perfect lesson bonus</span>
              </div>
            )}
            
            <span className="text-muted-foreground">Total XP earned</span>
            
            <div className="grid grid-cols-3 gap-4 w-full max-w-xs mt-4">
              {levels.map((level, index) => (
                <div key={index} className="flex flex-col items-center relative">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-1">
                    <span className="font-semibold">{index + 1}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">+{getAdjustedXpForLevel(index)} XP</span>
                  
                  {/* Show perfect level badge */}
                  {level.isPerfect && (
                    <div className="absolute -top-2 -right-2 bg-yellow-100 text-yellow-600 text-xs font-medium px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <Star className="h-3 w-3 fill-yellow-400 stroke-yellow-600" />
                      +5
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <Button 
            className="w-full" 
            size="lg"
            onClick={onBackToLessons}
          >
            Back to Lessons
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default LessonComplete;
